/**
 * One-colour line icons for the sidebar and homepage rows (they take the text colour).
 * Drawn on a 24px grid with 2px rounded strokes so they read as one set.
 */
import type { ReactElement } from 'react';
import type { Category } from '../games/catalog';

export type IconName = 'home' | 'solo' | 'multi' | 'new' | 'popular' | 'heart' | 'like' | 'plays' | 'calendar' | 'clock' | 'share' | 'keyboard' | 'flag' | 'book' | 'news' | Category;

const PATHS: Record<IconName, ReactElement> = {
  home: (
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9v11h14V9" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  solo: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
    </>
  ),
  multi: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.9 2.9-6.2 6.5-6.2s6.5 2.3 6.5 6.2" />
      <path d="M15.5 4.8a3.5 3.5 0 0 1 0 6.4" />
      <path d="M18 14.2c2.2.7 3.5 2.8 3.5 5.8" />
    </>
  ),
  new: (
    <>
      <path d="M12 3l2.4 5.6L20 9.3l-4.3 3.9 1.3 5.8L12 16l-5 3 1.3-5.8L4 9.3l5.6-.7z" />
    </>
  ),
  popular: (
    <>
      <path d="M12 21c-4 0-7-2.8-7-6.6 0-3.1 2-5.2 3.6-7 .4 1.8 1.4 3 2.6 3.6C11 7.6 12.6 5 15 3c.3 3 1.6 4.6 2.7 6.1 1.4 1.8 2.3 3.3 2.3 5.3C20 18.2 16 21 12 21z" />
    </>
  ),
  like: (
    <>
      <path d="M7 11v9H4.5A1.5 1.5 0 0 1 3 18.5v-6A1.5 1.5 0 0 1 4.5 11z" />
      <path d="M7 11l4-7.5c1.4 0 2.5 1.1 2.5 2.5v3.5h5a2 2 0 0 1 2 2.3l-1.2 6.5a2 2 0 0 1-2 1.7H7" />
    </>
  ),
  plays: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5v7l5.5-3.5z" fill="currentColor" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4.5c4-2 7 2 14 0v9c-7 2-10-2-14 0" />
    </>
  ),
  keyboard: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <path d="M6.5 10h1M10 10h1M13.5 10h1M17 10h.5M6.5 14h1M10 14h4.5M17 14h.5" />
    </>
  ),
  share: (
    <>
      <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
      <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </>
  ),
  book: (
    <>
      <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
      <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v3H6.5" />
      <path d="M9 7.5h7M9 11h5" />
    </>
  ),
  news: (
    <>
      <path d="M12 3l2.3 5.3L20 9l-4.3 3.8L17 18.5 12 15.6 7 18.5l1.3-5.7L4 9l5.7-.7z" />
    </>
  ),
  heart: <path d="M12 20s-8-4.7-8-10.5A4.5 4.5 0 0 1 12 6.6a4.5 4.5 0 0 1 8 2.9C20 15.3 12 20 12 20z" />,
  shooter: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" />
    </>
  ),
  sports: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.5 9.5c3 1 5.5 3.5 6.5 11.3M20.5 14.5c-3-1-5.5-3.5-6.5-11.3" />
      <path d="M5.6 5.6l12.8 12.8" />
    </>
  ),
  racing: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h13l-2.5 4.5L18 13H5" />
      <path d="M9.3 4v9M13.7 4v9M5 8.5h11.5" />
    </>
  ),
  arcade: (
    <>
      <rect x="3" y="14" width="18" height="7" rx="2" />
      <path d="M12 14V8" />
      <circle cx="12" cy="5.5" r="2.8" />
      <path d="M7 17.5h2" />
    </>
  ),
  clicker: (
    <>
      <path d="M9 9l11 4-4.6 1.6L13.8 19z" />
      <path d="M6.5 2.5l.7 2.6M2.5 6.5l2.6.7M11.7 3.5l-1.6 2M3.5 11.7l2-1.6" />
    </>
  ),
  sandbox: (
    <>
      <path d="M12 2.8l8.5 4.6v9.2L12 21.2l-8.5-4.6V7.4z" />
      <path d="M3.5 7.4 12 12l8.5-4.6M12 12v9.2" />
    </>
  ),
  party: (
    <>
      <path d="M3 21l5-13 8 8z" />
      <path d="M14 4.5c.8 1.3.8 2.7 0 4M19.5 10c-1.3-.8-2.7-.8-4 0M16.5 2.5v1.5M21.5 7.5H20M19 4.5l-1 1" />
    </>
  ),
  board: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3.5" />
      <circle cx="8.5" cy="8.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="15.5" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="15.5" cy="8.5" r="1" fill="currentColor" />
      <circle cx="8.5" cy="15.5" r="1" fill="currentColor" />
    </>
  ),
  strategy: (
    <>
      <path d="M6 3.5h2.5V6h2.25V3.5h2.5V6h2.25V3.5H18V8l-2 2v6l2 2.5V21H6v-2.5L8 16v-6L6 8z" />
      <path d="M8 16h8M8 10h8" />
    </>
  ),
  puzzle: (
    <path d="M4 8h3.5a2.5 2.5 0 1 1 5 0H16v3.5a2.5 2.5 0 1 1 0 5V20H12.5a2.5 2.5 0 1 0-5 0H4v-4.5a2.5 2.5 0 1 0 0-5z" />
  ),
};

export function NavIcon({ name, size = 22 }: { name: IconName; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}
